import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { SeattleGameState } from '../../types';
import { endGameSeattle } from '../utils/gameEndSeattle';

/**
 * Phase 10: ターン進行フェーズ（Seattle固有）
 *
 * ターンフラグをリセットし、次のターンに進みます。
 * 10ターン終了後はゲーム終了処理（清算・得点計算）を実行します。
 *
 * @see docs/specifications/logic/seattle_metro_solo/game_end_seattle.md
 */
export const advanceTurnSeattlePhase: PhaseConfig<SeattleGameState> = {
  onBegin: ({ G, ctx, events }: { G: SeattleGameState; ctx: any; events: any }) => {
    debug.log(`[advanceTurnSeattlePhase] Phase 10: Advance Turn started`);

    // ターン番号はPhase 1 (issueShares) でインクリメントされる
    const currentActualTurn = G.actualTurn;
    debug.log(`[advanceTurnSeattlePhase] Current actualTurn: ${currentActualTurn}`);

    // 10ターン終了判定
    const maxTurns = 10;
    if (currentActualTurn >= maxTurns) {
      debug.log(`[advanceTurnSeattlePhase] Game ending (actualTurn ${currentActualTurn} >= ${maxTurns})`);

      // ゲーム終了処理（清算・得点計算）
      endGameSeattle(G, ctx, events);

      G.autoPhaseResult = {
        phase: 'advanceTurn',
        message: 'Game Over - Final Settlement',
        floatingText: 'Game Over',
        details: { gameEnded: true }
      };
      return;
    }

    debug.log(`[advanceTurnSeattlePhase] Game continues (actualTurn ${currentActualTurn} < ${maxTurns})`);

    // プレイヤーのターンフラグをリセット
    const player = G.players['0'];

    player.selectedAction = null;
    player.actionsSelectedThisTurn = [];
    player.tilesPlacedThisTurn = 0;
    player.newCityTilesPlacedThisTurn = 0;
    player.hasPassedBuild = false;
    player.hasUpgradedLocoThisTurn = false;
    player.goodsPlacedThisTurn = 0;
    player.orderBidAmount = 0;
    player.turnsCompletedThisPhase = 0;
    player.goodsTransportedThisPhase = 0;
    player.hasPassed = false;
    player.hasExecutedIssueSharesMove = false;

    // Note: turnsCompletedThisPhase は共通カウンタ（上でリセット済み）
    // Issue #9 + Technical Debt #1: deliveriesThisTurn は廃止

    // 処理結果を保存（UI表示用）- 次ターン番号を表示
    const nextTurn = currentActualTurn + 1;
    G.autoPhaseResult = {
      phase: 'advanceTurn',
      message: 'Advancing to next turn...',
      floatingText: `Turn ${nextTurn}`,
      details: { newTurn: nextTurn }
    };

    debug.log(`[advanceTurnSeattlePhase] Turn flags reset, ready for Turn ${nextTurn}`);
  },

  moves: {
    proceedAutoPhase: ({ G, events }: { G: SeattleGameState; events: any }) => {
      G.autoPhaseResult = null;
      events.endPhase();
    }
  },

  endIf: ({ G }: { G: SeattleGameState }) => {
    // ゲーム終了時
    if (G.isGameOver) {
      return true;
    }
    return false;
  },

  onEnd: ({ G }: { G: SeattleGameState }) => {
    debug.log(`[advanceTurnSeattlePhase] Phase 10 ended`);

    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('advanceTurn');
  },

  next: 'issueShares'
};
